import {
  CreditCard,
  Banknote,
  Download,
  Upload,
  Clock,
  Landmark,
  ArrowUpFromLine,
  BarChart3,
  ArrowLeftRight,
  Users,
  TrendingUp,
  Coins,
  Copy,
  Target,
  TestTube,
} from "lucide-react";
import type { NavDropdownItem } from "./NavDropdownPanel";

export const cryptoItems: NavDropdownItem[] = [
  { icon: CreditCard, title: "Buy", description: "Buy crypto with card or fiat balance.", href: "/app/trade/buy" },
  { icon: Banknote, title: "Sell", description: "Sell crypto to get fiat instantly.", href: "/app/trade/sell" },
  { icon: Download, title: "Deposit", description: "Deposit crypto from supported networks.", href: "/app/crypto/deposit" },
  { icon: Upload, title: "Withdraw", description: "Withdraw crypto to your preferred wallet.", href: "/app/crypto/withdraw" },
  { icon: Clock, title: "History", description: "View your crypto wallet history.", href: "/app/transactions" },
];

export const fiatItems: NavDropdownItem[] = [
  { icon: Landmark, title: "Deposit", description: "Deposit fiat via online transfer or banks.", href: "/app/fiat/deposit" },
  { icon: ArrowUpFromLine, title: "Withdraw", description: "Withdraw fiat to your preferred method.", href: "/app/fiat/withdraw" },
  { icon: Clock, title: "History", description: "View your fiat wallet history.", href: "/app/transactions" },
];

export const tradeItems: NavDropdownItem[] = [
  { icon: BarChart3, title: "Spot", description: "Buy and sell on the spot market with advanced tools.", href: "/app/spot", moduleKey: "spot_trading" },
  { icon: ArrowLeftRight, title: "Convert", description: "The easiest way to trade at all sizes.", href: "/app/exchange", moduleKey: "wallets" },
  { icon: Users, title: "P2P", description: "Multiple fiat rails, zero fees, competitive prices.", href: "/app/p2p", moduleKey: "p2p" },
  { icon: TrendingUp, title: "Futures USD-M", description: "Trade perpetual contracts with USDT margin.", href: "/app/futures", moduleKey: "futures" },
  { icon: Coins, title: "Futures COIN-M", description: "Inverse contracts settled in coin.", href: "/app/futures?type=coinm", moduleKey: "futures" },
  { icon: Copy, title: "Copy Trading", description: "Follow top traders automatically.", href: "/app/copy-trading", moduleKey: "copy_trading" },
  { icon: Target, title: "Binary Trading", description: "Predict price direction and earn fixed payouts.", href: "/app/binary", moduleKey: "binary_trading" },
  { icon: TestTube, title: "Demo Trading", description: "Practice with simulated funds.", href: "/app/demo-wallet", moduleKey: "demo_trading" },
];

export const standaloneLinks = [
  { label: "Notes", href: "/notes", moduleKey: "landing_notes" },
  { label: "Markets", href: "/app/spot", moduleKey: "landing_markets" },
];

/**
 * Items that remain visible to logged-out (guest) visitors.
 * Sections not listed here are entirely hidden for guests (e.g. Fiat).
 * Keys are section labels; values are item titles to keep.
 */
export const GUEST_ALLOW: Record<string, string[]> = {
  Crypto: ["Buy", "Sell"],
  Trade: ["Spot", "P2P", "Futures USD-M", "Futures COIN-M"],
};

interface FilterableSection<T extends { title: string }> {
  label: string;
  items: T[];
}

/**
 * Apply guest allowlist when not authenticated. Drops empty sections.
 * When `isAuthed` is true, returns sections unchanged.
 */
export function filterMenusForAuth<T extends { title: string }, S extends FilterableSection<T>>(
  sections: S[],
  isAuthed: boolean
): S[] {
  if (isAuthed) return sections;
  return sections
    .filter((s) => Object.prototype.hasOwnProperty.call(GUEST_ALLOW, s.label))
    .map((s) => ({
      ...s,
      items: s.items.filter((item) => GUEST_ALLOW[s.label].includes(item.title)),
    }))
    .filter((s) => s.items.length > 0);
}
